import { useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { ClipTag, MediaItem, TagSource } from "@gooner/shared";
import { facetOf, tagDisplayName, tagLabel } from "@gooner/shared";
import { userApi } from "../../lib/api";
import { useSession } from "../../lib/session";
import { IconFlag, IconSparkle } from "../ui/icons";
import { cx } from "../ui/primitives";
import { hintTitle } from "./hintTitle";
import { browseHrefForTag } from "../lib/browseTagHref";

/**
 * THE keyword bubble. Every keyword the site renders as a pill goes through this —
 * clip previews, the admin panel, tag editors — so a keyword looks and behaves the
 * same everywhere and only has to learn a new trick once.
 *
 * Three shapes, chosen by which props you pass:
 *
 *   <TagBubble tag={t} />                  navigational: goes to /browse filtered by it (browseHrefForTag)
 *   <TagBubble tag={t} onRemove={fn} />    editable: adds a × that calls onRemove
 *   <TagBubble tag={t} active onSelect />  toggle: filter chip that can look selected
 *
 * `tag` takes either a bare slug or a full ClipTag. Only a ClipTag carries provenance,
 * so only that form can render the AI badge — a bare slug has nothing to claim.
 *
 * Display: the label is ALWAYS the resolved display name ("Doggy"), never the stored
 * slug ("pos:doggy"). The slug stays the identity — it's what /browse filters on and
 * what onRemove reports — because renaming a keyword must not break links or edits.
 *
 * The AI badge (see AiProvenanceBadge) hangs off the end of an ai-sourced keyword and
 * is its own button: clicking it opens the provenance popover instead of navigating.
 */

type TagBubbleTag = string | ClipTag;

function asClipTag(t: TagBubbleTag): ClipTag {
  return typeof t === "string"
    ? { tag: t, displayName: tagDisplayName(t), source: "admin" as TagSource }
    : t;
}

export function TagBubble({
  tag,
  hint,
  clip,
  onRemove,
  onSelect,
  active = false,
  onNavigate,
  onReported,
  className,
}: {
  tag: TagBubbleTag;
  /** Explanatory text for a keyword whose name does not say enough on its own — a
   *  clip-length bucket reads "Short" and means ">7–15s". Joins the label's title, so
   *  hovering anywhere on the pill shows `name — hint`. */
  hint?: string | null;
  /** The clip this keyword sits on. Required for the report flow — without it the
   *  badge still explains itself, it just can't offer "report". */
  clip?: MediaItem;
  /** Adds a × that calls this with the SLUG (not the display name). */
  onRemove?: (tag: string) => void;
  /** Makes the bubble a toggle instead of a link. Receives the slug. */
  onSelect?: (tag: string) => void;
  /** Toggle-variant selected state. */
  active?: boolean;
  /** Fires just before navigating — lets a modal close itself first. */
  onNavigate?: () => void;
  /** Fires after a report is accepted, so a parent can refresh. */
  onReported?: () => void;
  className?: string;
}) {
  const t = asClipTag(tag);
  // The label the browse sidebar uses: a FACET value gets its full contextual name
  // ("2 People", "Sex Focused", "Medium Tits") via tagLabel/facetValueLabel; a plain
  // keyword keeps its own displayName, which carries any admin override. The panel used
  // to show the raw displayName for everything, so facets read as bare "2" / "Sex".
  const label = facetOf(t.tag) !== null ? tagLabel(t.tag) : t.displayName;
  const navigate = useNavigate();
  const location = useLocation();

  // A toggle is always clickable. Otherwise the label navigates — EXCEPT on an
  // editor chip (one with a ×), where the keyword is being edited, not followed:
  // navigating away mid-edit would throw the rest of the form away.
  const interactive = Boolean(onSelect) || !onRemove;

  function activate() {
    if (onSelect) {
      onSelect(t.tag);
      return;
    }
    onNavigate?.();
    // On /browse a keyword narrows the current grid (filters + sort kept); elsewhere it
    // opens a fresh one. See browseHrefForTag.
    const onBrowse = location.pathname === "/browse";
    navigate(browseHrefForTag(t.tag, onBrowse ? new URLSearchParams(location.search) : null));
  }

  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 rounded-full border py-0.5 text-xs transition-colors",
        onRemove ? "pl-2.5 pr-1.5" : "px-2.5",
        active
          ? "border-transparent bg-[var(--color-accent)] text-white"
          : "border-[var(--color-edge)] bg-[var(--color-panel-2)] text-zinc-300",
        className,
      )}
    >
      {interactive ? (
        <button
          type="button"
          onClick={activate}
          title={hintTitle(
            onSelect
              ? `Filter by "${label}"`
              : `Browse all clips tagged "${label}"`,
            hint,
          )}
          className={cx(
            "max-w-[14rem] truncate transition-colors",
            active ? "text-white" : "hover:text-zinc-100",
          )}
        >
          {label}
        </button>
      ) : (
        <span className="max-w-[14rem] truncate" title={hintTitle(label, hint)}>
          {label}
        </span>
      )}

      {t.source === "ai" ? (
        <AiProvenanceBadge tag={t} clip={clip} onReported={onReported} />
      ) : null}

      {/* The "?" hint badge was here, and it was a second way to read something the pill
          already says. Hovering the KEYWORD shows `hintTitle(name, hint)` — the name and
          the explanation together — so the badge added a 3.5px target that revealed a
          subset of what the whole pill already revealed.
          Removing it also drops the one piece of this component that had to be inert and
          aria-hidden to avoid nesting a focusable control inside a button. The hint now
          reaches everybody the same way: on the control's own accessible name. */}

      {onRemove ? (
        <button
          type="button"
          onClick={() => onRemove(t.tag)}
          aria-label={`Remove ${label}`}
          className="leading-none text-zinc-500 transition-colors hover:text-[var(--color-accent-soft)]"
        >
          ×
        </button>
      ) : null}
    </span>
  );
}

/**
 * The little sparkle on an AI-assigned keyword, plus the popover explaining it.
 *
 * Opens on hover for pointer users AND on click, because hover doesn't exist on
 * touch — without the click path the report flow would be unreachable on a phone.
 * The badge is a button inside the bubble and stops propagation, so poking it never
 * navigates to the browse page by accident.
 */
function AiProvenanceBadge({
  tag,
  clip,
  onReported,
}: {
  tag: ClipTag;
  clip?: MediaItem;
  onReported?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const user = useSession((s) => s.user);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const popoverId = useId();

  // Dismiss on outside click / Escape, the same way the rest of the app's transient
  // surfaces behave.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  // A short close delay keeps the popover reachable: the pointer has to cross a gap
  // between the badge and the panel to click "Report".
  function scheduleClose() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  }
  function cancelClose() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }

  async function report() {
    if (!clip || state === "sending") return;
    setState("sending");
    try {
      await userApi.reportTag(clip, tag.tag);
      setState("done");
      onReported?.();
    } catch {
      setState("error");
    }
  }

  return (
    <span
      ref={wrapRef}
      className="relative inline-flex"
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-label={`${tag.displayName} was assigned by GoonerAI`}
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        className="inline-flex items-center text-[var(--color-accent-soft)] transition-colors hover:text-[var(--color-accent)]"
      >
        <IconSparkle width="10" height="10" />
      </button>

      {open ? (
        <span
          id={popoverId}
          role="dialog"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          className="absolute bottom-full left-1/2 z-50 mb-1.5 w-56 -translate-x-1/2 rounded-lg border border-[var(--color-edge)] bg-[var(--color-ink)] p-2.5 text-left shadow-xl"
        >
          <span className="block text-xs leading-snug text-zinc-300">
            This keyword was assigned by <span className="text-zinc-100">GoonerAI</span>.
          </span>

          {state === "done" ? (
            <span className="mt-2 block text-xs text-[var(--color-accent-soft)]">
              Thanks — sent for review.
            </span>
          ) : !clip ? null : !user ? (
            <span className="mt-2 block text-xs text-zinc-500">Sign in to report it.</span>
          ) : (
            <>
              <button
                type="button"
                onClick={report}
                disabled={state === "sending"}
                className="mt-2 inline-flex items-center gap-1 rounded-md border border-[var(--color-edge)] px-2 py-1 text-xs text-zinc-300 transition-colors hover:border-[var(--color-accent)]/60 hover:text-zinc-100 disabled:opacity-50"
              >
                <IconFlag width="12" height="12" />
                {state === "sending" ? "Reporting…" : "Report as mislabelled"}
              </button>
              {state === "error" ? (
                <span className="mt-1.5 block text-xs text-red-400">
                  Couldn&apos;t send that — try again.
                </span>
              ) : null}
            </>
          )}
        </span>
      ) : null}
    </span>
  );
}

/** Convenience wrapper for a wrapped row of bubbles — the layout every caller
 *  was hand-rolling with the same `flex flex-wrap gap-1.5`. */
export function TagBubbleRow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cx("flex flex-wrap gap-1.5", className)}>{children}</div>;
}
